import React, { useReducer, useState } from "react";
import PropTypes from "prop-types";
import { Button, Input, message, Card, Typography, Space } from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { reducer } from "../../../Utils/CommonUtils";
import { useFetch } from "../../../Utils/useFetch";
import { FETCH_ALL_ARTICLE_API } from "../../../Constants/Apis";
import { getArticlesWithColors } from "../../../Services/ArticleService";
import BarcodeTable from "../../elements/Tables/BarcodeTable/BarcodeTable";

const { Search } = Input;
const { Title, Text } = Typography;

const BarcodePage = ({ history, userData }) => {
  const initialState = {
    searchKeyword: "",
    filteredInfo: {},
    sorter: {},
    pagination: {
      current: 1,
      pageSize: 10,
      total: 0,
    },
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // Flatten pagination for API call
  const fetchParams = {
    searchKeyword: state.searchKeyword,
    filteredInfo: state.filteredInfo,
    sorter: state.sorter,
    ...state.pagination,
  };

  const { fetchedData, callReFetch, loading } = useFetch(
    `${FETCH_ALL_ARTICLE_API}/with-colors`,
    fetchParams
  );

  const data = fetchedData?.data?.data || [];
  const responsePagination = fetchedData?.data?.pagination;

  // Update pagination when API returns new total
  React.useEffect(() => {
    if (
      responsePagination &&
      responsePagination.total !== state.pagination.total
    ) {
      dispatch({
        type: "pagination",
        payload: {
          ...state.pagination,
          total: responsePagination.total,
        },
      });
    }
  }, [responsePagination, state.pagination.total]);

  const handleSearch = (value) => {
    dispatch({ type: "searchKeyword", payload: value });
    dispatch({
      type: "pagination",
      payload: { ...state.pagination, current: 1 },
    });
    callReFetch();
  };

  const handlePaginationChange = (pagination) => {
    dispatch({ type: "pagination", payload: pagination });
    callReFetch();
  };

  const handleRefresh = () => {
    callReFetch();
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloadLoading(true);
      message.loading("Preparing Excel file...", 0);

      const response = await getArticlesWithColors(
        state.searchKeyword,
        null,
        null,
        { current: 1, pageSize: 999999 }
      );
      const allData = response?.data?.data || [];

      const excelData = allData.map((item, index) => ({
        No: index + 1,
        "Article Name": item.name || "",
        Brand: item.brand || "",
        Category: item.category || "",
        Subcategory: item.subcategory || "",
        Division: item.division || "",
        Type: item.type || "",
        HPP: item.hpp || 0,
        Color: item.colorName || "No Color",
        Size: item.size || "",
        Barcode: item.barcode || "",
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      worksheet["!cols"] = [
        { wch: 5 },
        { wch: 30 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 12 },
        { wch: 12 },
        { wch: 8 },
        { wch: 20 },
      ];

      XLSX.utils.book_append_sheet(workbook, worksheet, "Barcodes");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `barcodes_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.destroy();
      const filterMessage = state.searchKeyword
        ? `Excel downloaded successfully! (${allData.length} records matching "${state.searchKeyword}")`
        : `Excel downloaded successfully! (${allData.length} records)`;
      message.success(filterMessage);
    } catch (error) {
      console.error("Error downloading Excel file:", error);
      message.destroy();
      message.error("Failed to download Excel file");
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div style={{ padding: "24px" }}>
      <Card
        title={
          <div>
            <Title level={3} style={{ margin: 0 }}>
              Barcode Management
            </Title>
            <Text type="secondary">
              View and manage article barcodes with color and size combinations
            </Text>
          </div>
        }
        extra={
          <Space wrap>
            <Search
              placeholder="Search articles..."
              allowClear
              enterButton={<SearchOutlined />}
              style={{ width: 250 }}
              onSearch={handleSearch}
              loading={loading}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadExcel}
              loading={downloadLoading}
            >
              Download Excel
            </Button>
          </Space>
        }
      >
        <BarcodeTable
          data={data}
          loading={loading}
          pagination={state.pagination}
          onPaginationChange={handlePaginationChange}
        />
      </Card>
    </div>
  );
};

BarcodePage.propTypes = {
  history: PropTypes.object,
  userData: PropTypes.object,
};

export default BarcodePage;
